import React, { useState, useEffect } from "react";
import apiClient from "../../../api/apiClient";
import Loading from "../../../components/Loading/Loading";

const AuditLog = () => {
    const [entries, setEntries] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [actionFilter, setActionFilter] = useState("");
    const [fromFilter, setFromFilter] = useState("");
    const [toFilter, setToFilter] = useState("");
    const LIMIT = 25;

    const fetchLog = async (p = page) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page: p, limit: LIMIT });
            if (actionFilter) params.set("action", actionFilter);
            if (fromFilter) params.set("from", fromFilter);
            if (toFilter) params.set("to", toFilter);
            const res = await apiClient.get(`/admin/audit-log?${params.toString()}`);
            setEntries(res.data.entries || []);
            setTotal(res.data.total || 0);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchLog(1); setPage(1); }, [actionFilter, fromFilter, toFilter]);

    const totalPages = Math.ceil(total / LIMIT);

    const ACTION_TYPES = [
        "USER_ACTIVATE", "USER_DEACTIVATE", "USER_LICENSE_ROLE_CHANGE",
        "USER_PLATFORM_ROLE_CHANGE", "USER_DELETE", "ADMIN_ACCOUNT_CREATE",
        "EMAIL_SEND", "APPROVAL_ACCEPT", "APPROVAL_DENY",
    ];

    return (
        <div>
            {/* Filters */}
            <div className="admin-audit-filters">
                <select
                    className="admin-audit-filter-input"
                    value={actionFilter}
                    onChange={(e) => setActionFilter(e.target.value)}
                >
                    <option value="">All Actions</option>
                    {ACTION_TYPES.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
                <input
                    type="date"
                    className="admin-audit-filter-input"
                    value={fromFilter}
                    onChange={(e) => setFromFilter(e.target.value)}
                    placeholder="From"
                />
                <input
                    type="date"
                    className="admin-audit-filter-input"
                    value={toFilter}
                    onChange={(e) => setToFilter(e.target.value)}
                    placeholder="To"
                />
                <span className="admin-search-count">{total} entries</span>
            </div>

            {loading ? (
                <div className="admin-loading-wrapper"><Loading /></div>
            ) : (
                <div className="admin-table-scroll">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th style={{ width: "18%" }}>Timestamp</th>
                                <th style={{ width: "22%" }}>Admin</th>
                                <th style={{ width: "18%" }}>Action</th>
                                <th style={{ width: "10%" }}>Target Type</th>
                                <th style={{ width: "18%" }}>Target</th>
                                <th style={{ width: "14%" }}>Details</th>
                            </tr>
                        </thead>
                        <tbody>
                            {entries.length === 0 && (
                                <tr><td colSpan={6} className="admin-table-empty">No audit log entries.</td></tr>
                            )}
                            {entries.map((e) => (
                                <tr key={e.id}>
                                    <td className="admin-td-secondary" style={{ whiteSpace: "nowrap" }}>
                                        {new Date(e.createdAt).toLocaleString()}
                                    </td>
                                    <td className="admin-td-secondary" style={{ wordBreak: "break-all", fontSize: "11px" }}>
                                        {e.adminUserId}
                                    </td>
                                    <td>
                                        <span className="admin-audit-action-badge">{e.action}</span>
                                    </td>
                                    <td className="admin-td-secondary">{e.targetType || "—"}</td>
                                    <td className="admin-td-secondary" style={{ wordBreak: "break-all", fontSize: "11px" }}>
                                        {e.targetId || "—"}
                                    </td>
                                    <td className="admin-td-secondary" style={{ fontSize: "11px" }}>
                                        {e.details ? (
                                            <details>
                                                <summary style={{ cursor: "pointer" }}>view</summary>
                                                <pre style={{ fontSize: "10px", whiteSpace: "pre-wrap", maxWidth: "200px" }}>
                                                    {JSON.stringify(typeof e.details === "string" ? JSON.parse(e.details) : e.details, null, 2)}
                                                </pre>
                                            </details>
                                        ) : "—"}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="admin-pagination">
                    <span className="admin-pagination-info">
                        Page {page} of {totalPages} ({total} entries)
                    </span>
                    <div className="admin-pagination-btns">
                        <button
                            className="admin-action-btn primary"
                            disabled={page <= 1}
                            onClick={() => { const p = page - 1; setPage(p); fetchLog(p); }}
                        >
                            Prev
                        </button>
                        <button
                            className="admin-action-btn primary"
                            disabled={page >= totalPages}
                            onClick={() => { const p = page + 1; setPage(p); fetchLog(p); }}
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AuditLog;
